import React, { useEffect, useMemo, useState } from "react";
import RightSideDrawer from "../../../../../components/reusable/RightSideDrawer";
import DrawerSection from "../../../../../components/reusable/DrawerSection";
import DetailRow from "../../../../../components/reusable/DetailRow";
import { caApi } from "../../../../../backend_urls/caApi";

// ============================================================
// EMPLOYEE
// ============================================================
const getEmployee = (task) => {
    const assignments =
        Array.isArray(task?.assignments) ? task.assignments : [];

    const assignment =
        assignments.find((item) => item?.role === "primary")
        || assignments[0];

    if (!assignment) {
        return {
            id: null,
            name: "Unassigned",
            role: "",
        };
    }

    const employee = assignment.employee || {};

    const user = employee.user || {};

    return {
        id:
            employee.id ||
            assignment.employee_id ||
            null,

        name:
            employee.name ||
            employee.employee_name ||
            assignment.employee_name ||
            user.user_full_name ||
            user.name ||
            user.email ||
            "Unknown",

        role:
            employee.role ||
            employee.designation ||
            assignment.employee_designation ||
            "",
    };
};

// ============================================================
// DATE VALUE
// ============================================================
const getDateInputValue = (value) => {
    if (!value) { return ""; }

    const date = new Date(value);

    if ( Number.isNaN(date.getTime()) ) { return ""; }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

// ============================================================
// DRAWER
// ============================================================
const TaskDrawer = ({
    isOpen,
    onClose,
    task,
    onWorkflow,
    onUpdateTask,
    workflowLoading,
    editLoading,
}) => {

    const [editing, setEditing] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [saveError, setSaveError] = useState("");

    const employee = useMemo(() => getEmployee(task), [task]);

    const [form, setForm] =
        useState({
            title: "",
            description: "",
            priority: "medium",
            due_date: "",
            employee_id: "",
        });

    // ========================================================
    // LOAD FORM
    // ========================================================
    useEffect(() => {
        if (!task) { return; }

        setForm({
            title: task.title || "",
            description: task.description || "",

            priority:
                task.raw_priority ||
                task.priority ||
                "medium",

            due_date:
                getDateInputValue(
                    task.due_date_raw ||
                    task.due_date
                ),

            employee_id:
                employee.id
                    ? String(employee.id)
                    : "",
        });
        setSaveError("");
    }, [ task, employee.id ]);

    // ========================================================
    // LOAD EMPLOYEES FOR EDIT
    // ========================================================
    const loadEmployees =
        async () => {
            try {
                setLoadingEmployees(true);
                setSaveError("");

                const response = await caApi.getTaskCreateOptions();

                const data = response?.data ?? response;

                setEmployees(
                    Array.isArray(data?.employees)
                        ? data.employees
                        : []
                );

            } catch (err) {
                console.error("Failed to load employees:", err);

                setSaveError(
                    err?.response?.data?.detail ||
                    "Unable to load employees."
                );

            } finally { setLoadingEmployees(false); }
        };

    // ========================================================
    // EDIT
    // ========================================================
    const handleEdit =
        async () => {
            setEditing(true);
            if (employees.length === 0) { await loadEmployees(); }
        };

    // ========================================================
    // FORM CHANGE
    // ========================================================
    const handleChange =
        (event) => {
            const { name, value } = event.target;
            setForm((current) => ({ ...current, [name]: value }));
            setSaveError("");
        };

    // ========================================================
    // SAVE
    // ========================================================
    const handleSave =
        async (event) => {
            event.preventDefault();
            if (!task?.id || editLoading) { return; }

            if (!form.title.trim()) {
                setSaveError("Task title is required.");
                return;
            }

            if (!form.employee_id) {
                setSaveError("Please assign an employee.");
                return;
            }

            try {
                setSaveError("");

                await onUpdateTask(
                    task.id,
                    {
                        title: form.title.trim(),
                        description: form.description.trim(),
                        priority: form.priority,
                        due_date: form.due_date,
                        assigned_employees: [ Number(form.employee_id) ],
                    }
                );

                setEditing(false);
            } catch (err) {
                setSaveError(
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    "Unable to update task."
                );
            }
        };

    // ========================================================
    // CANCEL EDIT
    // ========================================================
    const handleCancelEdit =
        () => {
            setEditing(false);
            setSaveError("");

            setForm({
                title: task?.title || "",
                description: task?.description || "",

                priority:
                    task?.raw_priority ||
                    task?.priority ||
                    "medium",

                due_date:
                    getDateInputValue(
                        task?.due_date_raw ||
                        task?.due_date
                    ),

                employee_id:
                    employee.id
                        ? String(employee.id)
                        : "",
            });
        };

    // ========================================================
    // WORKFLOW
    // ========================================================
    const runWorkflow =
        (action) => {
            if (!task?.id) { return; }
            onWorkflow(action, task, employee.id);
        };

    if (!task) { return null; }

    // ========================================================
    // STATUS
    // ========================================================
    const status = task.raw_status;

    const canStart =
        [
            "draft",
            "pending",
            "paused",
        ].includes(status);

    const canPause = status === "in_progress";
    const canResume = status === "paused";
    const canComplete = status === "in_progress";
        
    const canDraft  = status !== "completed" && status !== "draft";
    const canCancel = status !== "completed" && status !== "cancelled";

    // ========================================================
    // RENDER
    // ========================================================
    return (
        <RightSideDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={ editing ? "Edit Task" : "Task Overview" }
        >
            {!editing ? (
                <>
                    <DrawerSection title="General">
                        <DetailRow label="Task" value={ task.title } />

                        <DetailRow label="Business" value={ task.business } />

                        <DetailRow
                            label="Assigned To"
                            value={ employee.name || "Unassigned" }
                        />

                        <DetailRow label="Priority" value={ task.priority } />

                        <DetailRow label="Status" value={ task.status } />

                        <DetailRow
                            label="Progress"
                            value={`${task.progress || 0}%`}
                        />

                        <DetailRow label="Due Date" value={ task.due_date } />
                    </DrawerSection>

                    {task.description && (
                        <DrawerSection title="Description">
                            <div
                                style={{
                                    lineHeight: 1.6,
                                    color: "#475569",
                                    fontSize: 14,
                                }}
                            >
                                { task.description }
                            </div>
                        </DrawerSection>
                    )}

                    <DrawerSection title="Task Actions">
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                            }}
                        >
                            <button
                                type="button"
                                onClick={ handleEdit }
                                disabled={ workflowLoading }
                            >
                                Edit Task
                            </button>

                            {canStart && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow("start") }
                                >
                                    Start
                                </button>
                            )}

                            {canDraft && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow("draft") }
                                >
                                    Draft
                                </button>
                            )}

                            {canPause && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow("pause") }
                                >
                                    Pause
                                </button>
                            )}

                            {canResume && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow( "resume" ) }
                                >
                                    Resume
                                </button>
                            )}

                            {canComplete && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow("complete") }
                                >
                                    Complete
                                </button>
                            )}

                            {canCancel && (
                                <button
                                    type="button"
                                    disabled={ workflowLoading }
                                    onClick={() => runWorkflow( "cancel" ) }
                                >
                                    Cancel
                                </button>
                            )}
                        </div>

                        {workflowLoading && (
                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: 13,
                                    color: "#64748b",
                                }}
                            >
                                Updating task...
                            </div>
                        )}
                    </DrawerSection>
                </>
            ) : (

                // =================================================
                // EDIT FORM
                // =================================================
                <form onSubmit={ handleSave }>
                    <DrawerSection title="Task Details" >
                        {saveError && (
                            <div
                                style={{
                                    marginBottom: 15,
                                    padding: "10px 12px",
                                    borderRadius: 7,
                                    background: "#fef2f2",
                                    color: "#b91c1c",
                                    fontSize: 13,
                                }}
                            >
                                {saveError}
                            </div>
                        )}

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 15,
                            }}
                        >
                            <label>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    Task Title
                                </div>

                                <input
                                    type="text"
                                    name="title"
                                    value={ form.title }
                                    onChange={ handleChange }
                                    disabled={ editLoading }
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        padding: "10px 12px",
                                    }}
                                />
                            </label>

                            <label>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    Description
                                </div>

                                <textarea
                                    name="description"
                                    value={ form.description }
                                    onChange={ handleChange }
                                    rows={5}
                                    disabled={ editLoading }
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        padding: "10px 12px",
                                        resize: "vertical",
                                    }}
                                />
                            </label>

                            <label>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    Assigned Employee
                                </div>

                                <select
                                    name="employee_id"
                                    value={ form.employee_id }
                                    onChange={ handleChange }
                                    disabled={ editLoading || loadingEmployees }
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                    }}
                                >
                                    <option value="">
                                        {loadingEmployees
                                            ? "Loading employees..."
                                            : "Select employee"}
                                    </option>

                                    {employees.map(
                                        (item) => (
                                            <option
                                                key={ item.id }
                                                value={ item.id }
                                            >
                                                {
                                                    item.user_full_name ||
                                                    item.name ||
                                                    item.user_email ||
                                                    `Employee #${item.id}`
                                                }
                                            </option>
                                        )
                                    )}
                                </select>
                            </label>

                            <label>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    Priority
                                </div>

                                <select
                                    name="priority"
                                    value={ form.priority }
                                    onChange={ handleChange }
                                    disabled={ editLoading }
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                    }}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </label>

                            <label>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                >
                                    Due Date
                                </div>

                                <input
                                    type="date"
                                    name="due_date"
                                    value={ form.due_date }
                                    onChange={ handleChange }
                                    disabled={ editLoading }
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        boxSizing: "border-box",
                                    }}
                                />
                            </label>
                        </div>
                    </DrawerSection>

                    <DrawerSection title="Save Changes">
                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                            }}
                        >
                            <button
                                type="submit"
                                disabled={ editLoading }
                            >
                                {editLoading
                                    ? "Saving..."
                                    : "Save Changes"}
                            </button>

                            <button
                                type="button"
                                onClick={ handleCancelEdit }
                                disabled={ editLoading }
                            >
                                Cancel
                            </button>
                        </div>
                    </DrawerSection>
                </form>
            )}
        </RightSideDrawer>
    );
};

export default TaskDrawer;
